import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, SlidersHorizontal } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { formatRupiah, formatNumberInput, parseDigits } from "../lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AdjustExpenseModal({ open, onOpenChange, onApplied }) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [mode, setMode] = useState("percent");
  const [direction, setDirection] = useState("up");
  const [value, setValue] = useState(0);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    api.get("/categories?type=expense").then((r) => setCategories(r.data)).catch(() => {});
  }, [open]);

  const signedValue = () => {
    const v = mode === "percent" ? Number(value) : parseDigits(value);
    return direction === "up" ? v : -v;
  };

  const payload = () => ({
    date_from: dateFrom,
    date_to: dateTo,
    category,
    mode,
    value: signedValue(),
  });

  const doPreview = async () => {
    if (!signedValue()) {
      toast.error("Nilai penyesuaian tidak boleh 0");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/transactions/adjust-expenses", {
        ...payload(),
        action: "preview",
      });
      setPreview(data);
      if (data.count === 0) toast.info("Tidak ada pengeluaran yang cocok dengan filter");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const doApply = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/transactions/adjust-expenses", {
        ...payload(),
        action: "apply",
      });
      toast.success(
        `${data.updated} pengeluaran disesuaikan (${data.difference >= 0 ? "+" : ""}${formatRupiah(data.difference)})`
      );
      setPreview(null);
      onOpenChange(false);
      onApplied?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-adjust-expense" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-slate-200" /> Penyesuaian Nominal Pengeluaran
          </DialogTitle>
          <DialogDescription>
            Sesuaikan nominal beberapa pengeluaran sekaligus berdasarkan filter kategori dan
            periode. Saldo otomatis dihitung ulang.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Kategori</Label>
            <select
              data-testid="select-adjust-category"
              className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setPreview(null);
              }}
            >
              <option value="">Semua kategori</option>
              {categories.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Mode Penyesuaian</Label>
            <select
              data-testid="select-adjust-mode"
              className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setValue(0);
                setPreview(null);
              }}
            >
              <option value="percent">Persentase (%)</option>
              <option value="fixed">Nominal Tetap (Rp)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Dari Tanggal</Label>
            <Input
              data-testid="input-adjust-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPreview(null);
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Sampai Tanggal</Label>
            <Input
              data-testid="input-adjust-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPreview(null);
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Arah</Label>
            <select
              data-testid="select-adjust-direction"
              className="w-full rounded-lg border border-slate-700 bg-[#1E293B] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value);
                setPreview(null);
              }}
            >
              <option value="up">Naik (+)</option>
              <option value="down">Turun (−)</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Nilai {mode === "percent" ? "(%)" : "(Rp)"}</Label>
            <Input
              data-testid="input-adjust-value"
              className="font-num"
              inputMode="numeric"
              value={mode === "percent" ? value : formatNumberInput(value)}
              onChange={(e) => {
                setValue(mode === "percent" ? e.target.value.replace(/[^\d.,-]/g, "") : parseDigits(e.target.value));
                setPreview(null);
              }}
            />
          </div>
        </div>
        {preview && (
          <div
            data-testid="adjust-preview-result"
            className="rounded-lg border border-slate-800 bg-[#0F172A] p-4 text-sm space-y-1"
          >
            <p className="font-semibold text-slate-100">Pratinjau Penyesuaian</p>
            <p className="text-slate-400">
              {preview.count} transaksi akan diubah{preview.skipped > 0 ? `, ${preview.skipped} dilewati (hasil ≤ 0)` : ""}
            </p>
            <p className="font-num text-slate-400">
              {formatRupiah(preview.total_before)} →{" "}
              <span className="font-bold text-slate-100">{formatRupiah(preview.total_after)}</span>{" "}
              <span className={preview.difference >= 0 ? "text-emerald-400" : "text-rose-400"}>
                ({preview.difference >= 0 ? "+" : ""}
                {formatRupiah(preview.difference)})
              </span>
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Tutup
          </Button>
          {!preview ? (
            <Button
              data-testid="btn-adjust-preview"
              className="bg-slate-900 hover:bg-slate-800 text-white"
              onClick={doPreview}
              disabled={loading}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Pratinjau
            </Button>
          ) : (
            <Button
              data-testid="btn-adjust-apply"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={doApply}
              disabled={loading || preview.count === 0}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Terapkan ke {preview.count} Transaksi
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
